<script>
import Layout from "@/router/layouts/main";
import { VclList } from "vue-content-loading";
import { api } from "@/state/services";

export default {
  components: { Layout, VclList },

  data() {
    return {
      usina: null,
      loading: false,
      erroCarregar: '',
    }
  },

  computed: {
    idUsina() {
      return this.$route.params.id
    },

    isFotovoltaica() {
      return this.usina && this.usina.type === 'fotovoltaica'
    },

    labelSituacao() {
      const map = {
        homologada:  'Homologada e operando',
        homologacao: 'Em homologação',
        construcao:  'Em construção',
      }
      return map[this.usina && this.usina.situation] || (this.usina && this.usina.situation) || '—'
    },

    labelConnectionClass() {
      const map = {
        grupo_a: 'Grupo A — Mercado Livre',
        grupo_b: 'Grupo B — Geração Distribuída',
      }
      return map[this.usina && this.usina.connection_class] || '—'
    },

    labelHasCredits() {
      const map = { yes: 'Sim', no: 'Não', sometimes: 'Às vezes' }
      return map[this.usina && this.usina.has_credits] || '—'
    },

    labelOperationalSituation() {
      const map = {
        operando:              'Operando',
        parada:                'Parada',
        parcialmente_operando: 'Parcialmente operando',
      }
      return map[this.usina && this.usina.operational_situation] || '—'
    },

    labelStatus() {
      const map = { pending: 'Pendente', approved: 'Aprovado' }
      return map[this.usina && this.usina.status] || '—'
    },

    badgeStatus() {
      const map = { pending: 'warning', approved: 'success' }
      return 'badge badge-' + (map[this.usina && this.usina.status] || 'secondary')
    },

    enderecoCompleto() {
      if (!this.usina) return ''
      const parts = [
        this.usina.address,
        this.usina.number,
        this.usina.complement,
        this.usina.district,
        this.usina.city && this.usina.state ? `${this.usina.city}/${this.usina.state}` : '',
        this.usina.zipcode,
      ]
      return parts.filter(Boolean).join(', ')
    },
  },

  mounted() {
    this.carregarDados()
  },

  methods: {
    carregarDados() {
      this.loading = true
      this.erroCarregar = ''
      api
        .get('usinas/' + this.idUsina)
        .then(response => {
          if (response.data.status === 'success') {
            this.usina = response.data.usina
          } else {
            this.erroCarregar = response.data.message || 'Usina não encontrada.'
          }
        })
        .catch(() => {
          this.erroCarregar = 'Erro ao carregar os dados.'
        })
        .finally(() => {
          this.loading = false
        })
    },

    valorOu(v) {
      return v !== null && v !== undefined && v !== '' ? v : '—'
    },
  },
}
</script>

<template>
  <Layout>
    <ol class="breadcrumb">
      <li class="breadcrumb-item">
        <router-link tag="a" to="/">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
            <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
          </svg>
        </router-link>
      </li>
      <li class="breadcrumb-item">
        <router-link to="/usinas">Usinas</router-link>
      </li>
      <li class="breadcrumb-item active">Detalhes</li>
    </ol>

    <div v-if="loading" class="py-5 text-center">
      <vcl-list class="col-lg-6 mx-auto"></vcl-list>
    </div>

    <div v-else-if="erroCarregar" class="alert alert-danger">{{ erroCarregar }}</div>

    <template v-else-if="usina">
      <div class="page-title-box d-md-flex justify-content-between align-items-center">
        <div>
          <h3>
            <i :class="isFotovoltaica ? 'bx bx-sun text-warning mr-2' : 'bx bx-water text-primary mr-2'"></i>
            {{ isFotovoltaica ? 'Usina Fotovoltaica' : 'Usina Hídrica' }}
          </h3>
          <p class="text-muted mb-0">
            <span :class="badgeStatus" class="p-1 px-2 text-white text-uppercase" style="font-size: 0.75rem; border-radius: 4px;">
              {{ labelStatus }}
            </span>
            <span class="ml-2 text-muted small">Cadastrada em {{ usina.date_added ? new Date(usina.date_added).toLocaleDateString('pt-BR') : '' }}</span>
          </p>
        </div>
      </div>

      <!-- Dados principais -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bx bx-info-circle mr-1"></i>
            {{ isFotovoltaica ? 'Dados — Fotovoltaica' : 'Dados — Hídrica' }}
          </h5>
        </div>
        <div class="card-body">
          <div class="row">

            <div class="col-md-6">
              <dl class="row mb-0">
                <template v-if="isFotovoltaica">
                  <dt class="col-sm-5 text-muted">GD</dt>
                  <dd class="col-sm-7">{{ usina.gd_type ? usina.gd_type.toUpperCase() : '—' }}</dd>
                </template>

                <dt class="col-sm-5 text-muted">Potência instalada</dt>
                <dd class="col-sm-7">{{ valorOu(usina.installed_power) }}</dd>

                <dt class="col-sm-5 text-muted">Situação</dt>
                <dd class="col-sm-7">{{ labelSituacao }}</dd>

                <dt v-if="usina.situation === 'construcao'" class="col-sm-5 text-muted">Data prevista</dt>
                <dd v-if="usina.situation === 'construcao'" class="col-sm-7">{{ valorOu(usina.expected_date) }}</dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row mb-0">
                <dt class="col-sm-5 text-muted">Classe da conexão</dt>
                <dd class="col-sm-7">{{ labelConnectionClass }}</dd>

                <dt class="col-sm-5 text-muted">Média mensal (kWh/mês)</dt>
                <dd class="col-sm-7">{{ valorOu(usina.monthly_kwh) }}</dd>

                <template v-if="isFotovoltaica">
                  <dt class="col-sm-5 text-muted">Créditos sobrando</dt>
                  <dd class="col-sm-7">{{ labelHasCredits }}</dd>
                </template>

                <template v-else>
                  <dt class="col-sm-5 text-muted">Energia disponível</dt>
                  <dd class="col-sm-7">{{ valorOu(usina.available_energy) }}</dd>

                  <dt class="col-sm-5 text-muted">Situação operacional</dt>
                  <dd class="col-sm-7">{{ labelOperationalSituation }}</dd>
                </template>
              </dl>
            </div>

          </div>
        </div>
      </div>

      <!-- Distribuidora e Região -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bx bx-plug mr-1"></i> Concessionária
          </h5>
        </div>
        <div class="card-body">
          <dl class="row mb-0">
            <dt class="col-sm-3 text-muted">Distribuidora</dt>
            <dd class="col-sm-9">{{ valorOu(usina.distributor) }}</dd>

            <dt class="col-sm-3 text-muted">Localização da Usina</dt>
            <dd class="col-sm-9">{{ valorOu(usina.region) }}</dd>
          </dl>
        </div>
      </div>

      <!-- Endereço -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bx bx-map mr-1"></i> Endereço
          </h5>
        </div>
        <div class="card-body">
          <dl class="row mb-0">
            <dt class="col-sm-3 text-muted">Endereço completo</dt>
            <dd class="col-sm-9">{{ enderecoCompleto || '—' }}</dd>
          </dl>
        </div>
      </div>
    </template>
  </Layout>
</template>
